export interface TriggerFactory {
    createTrigger: (element: Element, callback: () => void) => Trigger;
}

export interface Trigger {
    trigger: () => void;
    stop: () => void;
}

export interface TriggerCondition {
    matches: () => boolean;
}

export class TimeWindowCondition implements TriggerCondition {

    public constructor(private start: number, private end: number) {
    }

    public matches(): boolean {
        const currentTime = Date.now();
        if (this.start > 0 && currentTime < this.start) {
            return false;
        }
        if (this.end > 0 && currentTime >= this.end) {
            return false;
        }
        return true;
    }

    public static timeWindowCondition(element: Element): TriggerCondition[] {
        if (!element.hasAttribute("data-trigger-start-datetime") && !element.hasAttribute("data-trigger-end-datetime")) {
            return [];
        }
        return [new TimeWindowCondition(
            Number.parseInt(element.getAttribute("data-trigger-start-datetime") ?? "0"),
            Number.parseInt(element.getAttribute("data-trigger-end-datetime") ?? "0")
        )];
    }
}

export abstract class PersistentTrigger implements Trigger {
    public triggered: boolean = false;
    public triggerCallbacks: (() => void)[];
    public triggerConditions: TriggerCondition[] = [];

    protected constructor(triggerConditions: TriggerCondition[], ...triggerCallbacks: (() => void)[]) {
        this.triggerCallbacks = triggerCallbacks;
        this.triggerConditions = triggerConditions;
    }

    public trigger(): void {
        if (!this.triggerConditions.every(condition => condition.matches())) {
            return;
        }
        this.triggered = true;
        this.triggerCallbacks.forEach(callback => callback());
    }

    public stop(): void {
        this.triggered = false;
    }
}

export class OnOffTrigger extends PersistentTrigger {
    public constructor(triggerConditions: TriggerCondition[], ...triggerCallbacks: (() => void)[]) {
        super(triggerConditions, ...triggerCallbacks);
    }

    public trigger(): void {
        super.trigger();
        this.triggered = false;
    }
}

export abstract class CombinationTrigger implements Trigger {
    public triggered: boolean = false;
    public triggerConditions: TriggerCondition[] = [];

    protected constructor(protected triggers: PersistentTrigger[], public triggeredCallback: () => void) {
        triggers.forEach(trigger => trigger.triggerCallbacks.push(() => this.checkState()));
        this.checkState();
    }

    public trigger(): void {
        this.triggered = true;
        this.triggeredCallback();
    }

    public stop(): void {
        this.triggered = false;
        this.triggers.forEach(trigger => trigger.stop());
    }

    private conditionMatches(): boolean {
        return this.triggerConditions.every(condition => condition.matches());
    }

    private checkState(): void {
        if (this.conditionMatches() && this.shouldTrigger()) {
            this.trigger();
        }
    }

    protected abstract shouldTrigger(): boolean;
}

export class AndTrigger extends CombinationTrigger {
    public constructor(triggers: PersistentTrigger[], callback: () => void, element: Element) {
        super(triggers, callback);
    }

    protected shouldTrigger(): boolean {
        return !this.triggered
            && this.triggers.every(trigger => trigger.triggered);
    }
}

export class OrTrigger extends CombinationTrigger {
    public constructor(triggers: PersistentTrigger[], callback: () => void, element: Element) {
        super(triggers, callback);
    }

    protected shouldTrigger(): boolean {
        return !this.triggered
            && this.triggers.some(trigger => trigger.triggered);
    }
}

export class SyntheticTriggerFactory implements TriggerFactory {
    public createTrigger(element: Element, callback: () => void): PersistentTrigger {
        return new SyntheticTrigger(element, [], callback);
    }
}

export class SyntheticTrigger extends PersistentTrigger {
    public constructor(protected element: Element, triggerConditions: TriggerCondition[], callback: () => void) {
        super(triggerConditions, callback);
        this.element.addEventListener("trigger", event => {
            event.preventDefault();
            this.trigger();
        });
    }
}